// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson — https://benrichardson.dev
// See ADDITIONAL-TERMS.md for the section 7(b) attribution requirement.
//
// countdown.ts — 3-2-1-GO between the round starting and the race actually starting (principle
// #15). Without it whoever happens to be looking at their screen gets a free move, and the board
// reads as a jump cut.
//
// setInterval, never rAF: a peer whose tab is backgrounded at round start would otherwise never
// count in at all, and the whole point is that everybody starts together.

import type { Sfx } from '@ben-gy/game-engine/sound';

export interface Countdown {
  cancel(): void;
}

export function runCountdown(host: HTMLElement, sfx: Sfx, done: () => void, stepMs = 620): Countdown {
  const el = document.createElement('div');
  el.className = 'countdown';
  el.setAttribute('aria-live', 'assertive');
  host.appendChild(el);

  const seq = ['3', '2', '1', 'Go'];
  let i = 0;
  let timer: ReturnType<typeof setInterval> | null = null;
  let finished = false;

  const show = (): void => {
    el.textContent = seq[i];
    el.classList.remove('pulse');
    void el.offsetWidth; // restart the animation
    el.classList.add('pulse');
    // The audio is what actually carries this: players watch the common, not the overlay.
    sfx.play(i === seq.length - 1 ? 'go' : 'beat');
  };

  const finish = (): void => {
    if (finished) return;
    finished = true;
    if (timer) clearInterval(timer);
    timer = null;
    el.remove();
    done();
  };

  show();
  timer = setInterval(() => {
    i++;
    if (i >= seq.length) {
      setTimeout(finish, stepMs * 0.6);
      if (timer) clearInterval(timer);
      timer = null;
      return;
    }
    show();
  }, stepMs);

  return {
    cancel: () => {
      if (timer) clearInterval(timer);
      timer = null;
      finished = true;
      el.remove();
    },
  };
}
